/**
 * @module AssetLoader
 * Loads all game assets (SVG textures + audio) before the game starts.
 *
 * Texture keys follow the ADR-004 convention:
 *   fruits:   `${fruitType}-whole`, `${fruitType}-half-a`, `${fruitType}-half-b`
 *   obstacles: `bomb`, `ice`, `rotten`
 *   powerups:  `freeze`, `double`, `magnet`
 *
 * SVGs are loaded as HTMLImageElement objects via the Image API and rasterised
 * at 200×200 px to match PixiJS texture expectations.
 *
 * Audio files are registered by name so the AudioManager's SoundPlayer
 * implementation (HowlerPlayer) can reference them.
 *
 * This module is PixiJS-agnostic — it works with any renderer that accepts
 * an HTMLImageElement. PixiJS's Assets.load() is called in main.ts.
 */

import type { FruitType } from './game-config.js';

// ---------------------------------------------------------------------------
// Texture key constants (ADR-004)
// ---------------------------------------------------------------------------

/** All valid texture key suffixes for fruit sprites. */
const FRUIT_SUFFIXES = ['whole', 'half-a', 'half-b'] as const;

/** All fruit types — must match CONFIG.fruits keys. */
const FRUIT_TYPES: FruitType[] = [
  'apel', 'semangka', 'jeruk', 'pisang',
  'stroberi', 'nanas', 'kiwi', 'naga',
];

/** Obstacle entity texture keys. */
const OBSTACLE_KEYS = ['bomb', 'ice', 'rotten'] as const;

/** Power-up entity texture keys. */
const POWERUP_KEYS = ['freeze', 'double', 'magnet'] as const;

/** All sound names the AudioManager references. */
export const SOUND_KEYS = [
  'slash',
  'splat',
  'bomb',
  'combo',
  'powerup',
  'ui',
  'frenzy',
] as const;
export type SoundKey = typeof SOUND_KEYS[number];

// Map AudioManager sound name → actual file basename.
const SOUND_FILE_MAP: Record<SoundKey, string> = {
  slash: 'slash',
  splat: 'fruit-hit',
  bomb: 'bomb',
  combo: 'combo',
  powerup: 'power-freeze', // generic; specific variants played on type
  ui: 'ui-click',
  frenzy: 'frenzy-start',
};

// ---------------------------------------------------------------------------
// Asset manifest
// ---------------------------------------------------------------------------

/** A texture entry: logical key → URL path. */
export interface TextureEntry {
  key: string;
  url: string;
}

/** A sound entry: logical name → URL path. */
export interface SoundEntry {
  name: SoundKey;
  url: string;
}

/** Generates the full texture manifest from fruit/obstacle/powerup conventions. */
export function buildTextureManifest(): TextureEntry[] {
  const entries: TextureEntry[] = [];

  // Fruit textures: 8 types × 3 variants = 24 entries.
  for (const type of FRUIT_TYPES) {
    for (const suffix of FRUIT_SUFFIXES) {
      const key: string = `${type}-${suffix}`;
      entries.push({
        key,
        url: `/assets/fruits/${key}.svg`,
      });
    }
  }

  // Obstacle textures.
  for (const key of OBSTACLE_KEYS) {
    entries.push({ key, url: `/assets/obstacles/${key}.svg` });
  }

  // Power-up textures.
  for (const key of POWERUP_KEYS) {
    entries.push({ key, url: `/assets/powerups/${key}.svg` });
  }

  return entries;
}

/** Generates the full sound manifest. */
export function buildSoundManifest(): SoundEntry[] {
  return SOUND_KEYS.map((name) => ({
    name,
    url: `/assets/sounds/${SOUND_FILE_MAP[name]}.wav`,
  }));
}

// ---------------------------------------------------------------------------
// Texture loading (HTML Image API — PixiJS-agnostic)
// ---------------------------------------------------------------------------

/** Loaded texture registry: key → HTMLImageElement. */
export type TextureRegistry = Map<string, HTMLImageElement>;

/**
 * Loads all SVG textures and returns a registry keyed by texture key.
 * Each SVG is rasterised at 200×200 px via HTMLImageElement.
 *
 * @param onProgress - Optional callback with ratio 0.0–1.0.
 */
export async function loadTextures(
  onProgress?: (ratio: number) => void,
): Promise<TextureRegistry> {
  const manifest = buildTextureManifest();
  const registry: TextureRegistry = new Map();
  let loaded = 0;

  await Promise.all(
    manifest.map(({ key, url }) =>
      new Promise<void>((resolve) => {
        const img = new Image(200, 200);
        img.onload = () => {
          registry.set(key, img);
          loaded++;
          onProgress?.(loaded / manifest.length);
          resolve();
        };
        img.onerror = () => {
          // Non-fatal: missing asset logs a warning and resolves with a placeholder.
          console.warn(`[AssetLoader] Failed to load texture: ${url}`);
          loaded++;
          onProgress?.(loaded / manifest.length);
          resolve();
        };
        img.src = url;
      }),
    ),
  );

  return registry;
}

// ---------------------------------------------------------------------------
// Fruit key helpers
// ---------------------------------------------------------------------------

/** Returns the whole-fruit texture key for the given fruit type. */
export function fruitWholeKey(type: FruitType): string {
  return `${type}-whole`;
}

/** Returns the half-A texture key for the given fruit type. */
export function fruitHalfAKey(type: FruitType): string {
  return `${type}-half-a`;
}

/** Returns the half-B texture key for the given fruit type. */
export function fruitHalfBKey(type: FruitType): string {
  return `${type}-half-b`;
}
